<template>
  <div class="crypto-currency-select">
    <div class="crypto-currency-select__header">
      <div class="crypto-currency-select__close">
        <IconClose @click="emit('close')" />
      </div>
      <div class="crypto-currency-select__search">
        <FilterInput v-model="searchCryptoCurrency" />
      </div>
      <div class="crypto-currency-select__sub-heading">
        {{ i18n.t(`exchange-form.drop-down.crypto-title.${props.flow}`) }}
      </div>
    </div>
    <div v-if="props.loading" class="crypto-currency-select__loading">
      <ExchangeLoading />
    </div>
    <PbScroll v-else>
      <div class="crypto-currency-select__list">
        <CryptoCurrency
          v-for="(option, index) in items"
          :key="index"
          :selected="props.value.currency === option.currency"
          :currency="option.currency"
          :icon="option.icon"
          :label="option.name"
          @click.native="selectHandler(option)"
        />
      </div>
    </PbScroll>
  </div>
</template>

<script lang="ts" setup>
import { computed, onUnmounted, ref } from 'vue';

import CryptoCurrency from '@/components/ExchangeFormV2/CryptoCurrency.vue';
import ExchangeLoading from '@/components/ExchangeFormV2/ExchangeLoading.vue';
import FilterInput from '@/components/ExchangeFormV2/FilterInput.vue';
import PbScroll from '@/components/ExchangeFormV2/PbScroll.vue';
import { i18n } from '@/i18n';
import type { CryptoOptionModel } from '@/models';
import { BaseOptionModel } from '@/models';
import type { Flow } from '@/types';

import IconClose from '../../../assets/images/exchange-form/close.svg';

const props = defineProps<{
  value: BaseOptionModel,
  flow: Flow
  items: CryptoOptionModel[]
  loading: boolean
  flowSwitchDisabled?: boolean
}>();

const emit = defineEmits<{
  (event: 'close'): void,
  (event: 'input', option: CryptoOptionModel): void,
}>();

const searchCryptoCurrency = ref('');
onUnmounted(() => searchCryptoCurrency.value = '');

const items = computed(() => {
  if (searchCryptoCurrency.value.length) {
    const search = searchCryptoCurrency.value.toLowerCase();
    return props.items.filter(item => {
      return item.currency.toLowerCase().includes(search) || item.name.toLowerCase().includes(search);
    });
  }

  return props.items;
});

const selectHandler = (option: CryptoOptionModel) => {
  emit('input', option);
  emit('close');
};
</script>

<style lang="scss" scoped>
@import "@scss-vars";

.crypto-currency-select {
  display: flex;
  flex-direction: column;
  height: 100%;

  &__header {
    display: grid;
    grid-gap: 16px;
    justify-content: stretch;
    align-items: center;
    grid-template-areas: "search" "sub-heading";
    padding: 16px 16px 6px;
    grid-template-columns: 1fr;

    @media screen and (max-width: $screen-lg-min) {
      padding: 20px 24px 8px;
    }

    @media screen and (max-width: $screen-md-max) {
      grid-template-areas:
        "search close"
        "sub-heading sub-heading";
      grid-template-columns: 1fr max-content;
    }
  }

  &__search {
    grid-area: search;
  }

  &__sub-heading {
    grid-area: sub-heading;
    font-family: $font-bold;
    font-size: rem(14);
    color: $black-50;
  }

  &__close {
    grid-area: close;
    justify-self: end;
    display: none;
    width: rem(24);
    height: rem(24);

    [dir="rtl"] & {
      margin-right: 4px;
      margin-left: unset;
    }

    & svg {
      width: rem(24);
      height: rem(24);
    }

    @media screen and (max-width: $screen-lg-min) {
      display: block;
    }
  }

  &__list {
    margin: 0 8px;
    padding-bottom: 8px;
  }

  &__loading {
    display: flex;
    align-items: center;
    justify-content: center;
  }
}
</style>
